import React, { useState } from 'react';
import { Button, Alert } from 'react-bootstrap';
import { CalendarPlus, AlertCircle } from 'lucide-react';

import SettingsModal from '../Settings/SettingsModal';
import SettingsSelect from '../Settings/SettingsSelect';
import InfoHint from '../SharedComponents/InfoHint';

/**
 * Skapa nytt räkenskapsår.
 *
 * Was four fields wearing three nested boxes: two bordered "sections" with
 * their own icon headings, and a green info card inside the first — all inside
 * a modal that is itself a box. It also opened with both selects outlined in
 * red and two "obligatorisk" messages showing, because a useEffect ran the
 * validator on mount, and it offered a "Återställ" button next to "Avbryt" in
 * a dialog you can simply close.
 *
 * Now: one flat form, the derived year/length as a single quiet line, errors
 * only after you try to submit.
 */

const ACCOUNTING_METHOD_OPTIONS = [
  { value: 'accrual', label: 'Faktureringsmetoden', description: 'Intäkter och kostnader bokförs när de uppstår' },
  { value: 'cash', label: 'Kontantmetoden', description: 'Intäkter och kostnader bokförs när betalning sker' },
];

const VAT_PERIOD_OPTIONS = [
  { value: 'monthly', label: 'Månadsvis', description: 'Momsredovisning varje månad' },
  { value: 'quarterly', label: 'Kvartalsvis', description: 'Momsredovisning varje kvartal' },
  { value: 'yearly', label: 'Årsvis', description: 'Momsredovisning en gång per år' },
];

// The explanation belongs in the menu, where it helps you choose. react-select
// runs this for the selected value too (context 'value'), where a second line
// does not fit a 34px control — there, just the label.
const renderOption = (option, { context }) => {
  if (context === 'value') return option.label;
  return (
    <div>
      <div style={{ fontWeight: 500 }}>{option.label}</div>
      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{option.description}</div>
    </div>
  );
};

const CreateFiscalYearModal = ({ show, handleClose, handleSave, openFiscalYearsCount = 0 }) => {
  const currentYear = new Date().getFullYear();

  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState(`${currentYear}-12-31`);
  const [accountingMethod, setAccountingMethod] = useState(null);
  const [vatPeriod, setVatPeriod] = useState(null);
  // Nothing is wrong until you have tried to submit — the form opened shouting
  // at the user before.
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const blocked = openFiscalYearsCount >= 2;

  const errors = {};
  if (!startDate) errors.startDate = 'Startdatum är obligatoriskt';
  if (!endDate) errors.endDate = 'Slutdatum är obligatoriskt';
  if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
    errors.dateRange = 'Slutdatum måste vara efter startdatum';
  }
  if (!accountingMethod) errors.accountingMethod = 'Välj en bokföringsmetod';
  if (!vatPeriod) errors.vatPeriod = 'Välj en momsperiod';

  const isValid = Object.keys(errors).length === 0;
  const show_ = (key) => submitted && errors[key];

  const fiscalInfo = (() => {
    if (!startDate || !endDate || errors.dateRange) return null;
    const start = new Date(startDate);
    const days = Math.ceil(Math.abs(new Date(endDate) - start) / 86400000) + 1;
    return { year: start.getFullYear(), days, isStandardYear: days >= 365 && days <= 366 };
  })();

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!isValid) return;
    setIsLoading(true);
    try {
      await handleSave({ startDate, endDate, accountingMethod, vatPeriod });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SettingsModal
      show={show}
      onHide={handleClose}
      closeButton={!isLoading}
      icon={CalendarPlus}
      title="Skapa nytt räkenskapsår"
      footer={(
        <>
          <Button variant="outline-secondary" onClick={handleClose} disabled={isLoading}>
            Avbryt
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isLoading || blocked}>
            {isLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Sparar…
              </>
            ) : 'Skapa räkenskapsår'}
          </Button>
        </>
      )}
    >
      {blocked && (
        <Alert variant="warning" className="d-flex align-items-start gap-2 mb-3">
          <AlertCircle size={16} className="flex-shrink-0 mt-1" />
          <div>
            <strong>Det finns redan {openFiscalYearsCount} öppna räkenskapsår.</strong>
            <br />
            Stäng minst ett tidigare räkenskapsår innan du skapar ett nytt.
          </div>
        </Alert>
      )}

      <div className="form-fields">
        <div className="form-field half-width">
          <label htmlFor="fyStart" className="modern-label">Startdatum *</label>
          <input
            id="fyStart"
            type="date"
            className={`modern-input ${show_('startDate') || show_('dateRange') ? 'error' : ''}`}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          {show_('startDate') && <div className="field-error">{errors.startDate}</div>}
        </div>

        <div className="form-field half-width">
          <label htmlFor="fyEnd" className="modern-label">Slutdatum *</label>
          <input
            id="fyEnd"
            type="date"
            className={`modern-input ${show_('endDate') || show_('dateRange') ? 'error' : ''}`}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          {show_('endDate') && <div className="field-error">{errors.endDate}</div>}
          {show_('dateRange') && <div className="field-error">{errors.dateRange}</div>}
        </div>

        <div className="form-field half-width">
          <label htmlFor="fyMethod" className="modern-label">
            Bokföringsmetod *
            <InfoHint text="Styr när en affärshändelse hamnar i bokföringen. Faktureringsmetoden bokför den när fakturan skapas; kontantmetoden när betalningen sker. Valet gäller för hela räkenskapsåret." />
          </label>
          <SettingsSelect
            id="fyMethod"
            options={ACCOUNTING_METHOD_OPTIONS}
            value={accountingMethod}
            onChange={setAccountingMethod}
            placeholder="Välj bokföringsmetod…"
            formatOptionLabel={renderOption}
          />
          {show_('accountingMethod') && <div className="field-error">{errors.accountingMethod}</div>}
        </div>

        <div className="form-field half-width">
          <label htmlFor="fyVat" className="modern-label">
            Momsperiod *
            <InfoHint text="Hur ofta momsen redovisas till Skatteverket — varje månad, varje kvartal eller en gång per år. Perioden styr vilka momsdeklarationer som skapas för räkenskapsåret." />
          </label>
          <SettingsSelect
            id="fyVat"
            options={VAT_PERIOD_OPTIONS}
            value={vatPeriod}
            onChange={setVatPeriod}
            placeholder="Välj momsperiod…"
            formatOptionLabel={renderOption}
          />
          {show_('vatPeriod') && <div className="field-error">{errors.vatPeriod}</div>}
        </div>
      </div>

      {/* Derived from the dates above — one line, not a card. */}
      {fiscalInfo && (
        <p className="settings-modal__note mt-3 mb-0">
          {fiscalInfo.year} · {fiscalInfo.days} dagar
          {!fiscalInfo.isStandardYear && ' · avviker från ett standardår (365–366 dagar)'}
        </p>
      )}
    </SettingsModal>
  );
};

export default CreateFiscalYearModal;
